import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/lib/api';
import {
  LayoutDashboard, Box, ClipboardList, ShoppingCart,
  FileText, CheckSquare, BarChart3, Users, Bell,
  LogOut, ChevronLeft, ChevronRight,
  Scale, GitMerge, UserCircle, Truck, Menu, X, ShoppingBag, Tag, UserCheck, FileBarChart, ClipboardCheck, History, Shield, KeyRound
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'store_manager', 'stock_manager', 'billing_executive'] },
  { path: '/boxes', label: 'Stock / Boxes', icon: Box, roles: ['admin', 'store_manager', 'stock_manager'] },
  { path: '/stock-taking', label: 'Stock Taking', icon: Scale, roles: ['admin', 'store_manager', 'stock_manager', 'billing_executive'] },
  { path: '/stock-taking-results', label: 'ST Results', icon: ClipboardCheck, roles: ['admin', 'store_manager'] },
  { path: '/daily-ops', label: 'Daily Ops', icon: ShoppingCart, roles: ['admin', 'store_manager', 'stock_manager'] },
  { path: '/bills', label: 'Bills', icon: FileText, roles: ['admin', 'store_manager', 'billing_executive', 'stock_manager'] },
  { path: '/approvals', label: 'Approvals', icon: CheckSquare, roles: ['admin', 'store_manager'], badge: true },
  { path: '/reconciliation', label: 'Reconciliation', icon: ClipboardList, roles: ['admin', 'store_manager'] },
  { path: '/customers', label: 'Customers', icon: UserCircle, roles: ['admin', 'store_manager'], denyAnalyst: true },
  { path: '/suppliers', label: 'Karigars', icon: Truck, roles: ['admin', 'store_manager'] },
  { path: '/items', label: 'Item Names', icon: Tag, roles: ['admin', 'store_manager'] },
  { path: '/salesmen', label: 'Salesmen', icon: UserCheck, roles: ['admin', 'store_manager'] },
  { path: '/box-merge', label: 'Box Merging', icon: GitMerge, roles: ['admin', 'store_manager', 'stock_manager'] },
  { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingBag, roles: ['admin', 'store_manager'] },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['admin', 'store_manager'] },
  { path: '/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'store_manager'] },
  { path: '/notifications', label: 'Notifications', icon: Bell, roles: ['admin', 'store_manager'], badge: true },
  { path: '/users', label: 'Users', icon: Users, roles: ['admin'], denyAnalyst: true },
  { path: '/data-safety', label: 'Data Safety', icon: Shield, roles: ['admin'] },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [currentTime, setCurrentTime] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit', minute: '2-digit',
        day: '2-digit', month: 'short',
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canSeeApprovals = ['admin', 'store_manager', 'analyst'].includes(user?.role);
    const fetchCounts = async () => {
      try {
        const promises = [API.get('/notifications/count')];
        if (canSeeApprovals) promises.unshift(API.get('/approvals/count'));
        const results = await Promise.all(promises);
        if (canSeeApprovals) {
          setPendingApprovals(results[0].data.count);
          setNotifCount(results[1].data.count);
        } else {
          setNotifCount(results[0].data.count);
        }
      } catch(e) { /* silent */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredNav = navItems.filter(item => {
    if (user?.role === 'analyst') return !item.denyAnalyst && (item.roles.includes('analyst') || item.roles.includes('admin'));
    return item.roles.includes(user?.role);
  });

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleChangePassword = async () => {
    if (!pwForm.old_password || !pwForm.new_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (pwForm.new_password.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      await API.post('/auth/change-password', {
        old_password: pwForm.old_password,
        new_password: pwForm.new_password,
      });
      toast.success('Password changed successfully');
      setShowChangePw(false);
      setPwForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const toggleMobileSidebar = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const roleLabels = {
    admin: 'Administrator',
    analyst: 'Analyst',
    store_manager: 'Store Manager',
    billing_executive: 'Billing Executive',
    stock_manager: 'Stock Manager',
  };

  const sidebarClasses = [
    'app-sidebar',
    isMobile && mobileOpen ? 'mobile-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="app-layout">
      <div className={`sidebar-overlay ${mobileOpen ? 'visible' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className={sidebarClasses} data-testid="app-sidebar">
        <div className="sidebar-logo flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/yash-gold-logo.png" alt="The Yash Gold App" className="h-12 w-auto logo-glow" />
            <div>
              <h1 className="text-sm font-bold" style={{ fontFamily: 'var(--font-heading)', color: 'hsl(43, 96%, 56%)' }}>The Yash Gold</h1>
              <p className="text-[10px]" style={{ color: 'hsl(40, 12%, 55%)' }}>App</p>
            </div>
          </div>
          {isMobile && (
            <button onClick={() => setMobileOpen(false)} className="text-white/60 hover:text-white p-1">
              <X size={20} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {filteredNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
              data-testid={`sidebar-nav-item-${item.path.slice(1)}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
              {item.badge && item.path === '/approvals' && pendingApprovals > 0 && (
                <span className="sidebar-nav-badge">{pendingApprovals}</span>
              )}
              {item.badge && item.path === '/notifications' && notifCount > 0 && (
                <span className="sidebar-nav-badge">{notifCount > 99 ? '99+' : notifCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t" style={{ borderColor: 'hsl(43, 96%, 56%, 0.1)' }}>
          <button
            onClick={() => setShowChangePw(true)}
            className="sidebar-nav-item w-full text-amber-400 hover:text-amber-300 mb-1"
            data-testid="change-password-button"
          >
            <KeyRound size={20} />
            <span>Change Password</span>
          </button>
          <button
            onClick={handleLogout}
            className="sidebar-nav-item w-full text-red-400 hover:text-red-300"
            data-testid="logout-button"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="app-main">
        <header className="app-topbar">
          {isMobile ? (
            <Button variant="ghost" size="icon" onClick={toggleMobileSidebar} className="mr-2" data-testid="mobile-menu-button">
              <Menu size={22} />
            </Button>
          ) : (
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="mr-4" data-testid="back-button">
              <ChevronLeft size={18} />
            </Button>
          )}

          {isMobile && (
            <img src="/yash-gold-logo.png" alt="YG" className="h-16 w-auto mr-2 logo-glow" />
          )}

          <div className="flex-1" />

          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-xs md:text-sm font-mono tabular-nums text-muted-foreground hidden sm:inline" data-testid="ist-clock">
              {currentTime} IST
            </span>
            <span
              className="px-2 md:px-3 py-1 rounded-full text-xs font-semibold border truncate max-w-[120px] md:max-w-none"
              style={{ background: 'hsl(43, 50%, 14%)', color: 'hsl(43, 80%, 65%)', borderColor: 'hsl(43, 50%, 25%)' }}
              data-testid="current-role-chip"
            >
              {roleLabels[user?.role] || user?.role}
            </span>
            <span className="text-sm font-medium hidden md:inline">{user?.name}</span>
          </div>
        </header>

        <div className="app-content">
          {children}
        </div>
      </main>

      <Dialog open={showChangePw} onOpenChange={(open) => { setShowChangePw(open); if (!open) setPwForm({ old_password: '', new_password: '', confirm_password: '' }); }}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="old_password">Current Password</Label>
              <Input
                id="old_password"
                type="password"
                value={pwForm.old_password}
                onChange={(e) => setPwForm({ ...pwForm, old_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="change-pw-old-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                placeholder="Enter new password"
                data-testid="change-pw-new-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                data-testid="change-pw-confirm-input"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={pwLoading}
              className="w-full"
              data-testid="change-pw-submit-button"
            >
              {pwLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
